import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useListClaudeSubmissionsQuery,
  useUpdateClaudeSubmissionStatusMutation,
  useGetClaudeCertificateUrlMutation,
  useListUnmatchedClaudeAccountsQuery,
} from '../app/api';
import UsageTierChart, { TierBadge, ClaudeCodeMark, USAGE_TIERS, matchesTier } from './UsageTierChart';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const STATUS_STYLES = {
  completed:   { bg: 'rgba(26,127,78,0.12)',  fg: brand.green,     br: 'rgba(26,127,78,0.3)' },
  pending:     { bg: 'rgba(208,155,44,0.15)', fg: brand.goldenrod, br: 'rgba(208,155,44,0.3)' },
  not_started: { bg: 'rgba(88,88,91,0.10)',   fg: brand.charcoal,  br: 'rgba(88,88,91,0.3)' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span style={{
      background: s.bg,
      color: s.fg,
      border: `1px solid ${s.br}`,
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    }}>
      {status === 'not_started' ? 'not started' : status}
    </span>
  );
}

StatusBadge.propTypes = { status: PropTypes.string.isRequired };

// Live Claude-account usage for the current month (not persisted). Computed
// server-side from Anthropic's usage report across the person's current +
// prior + alias emails. Dollar amounts are intentionally hidden.
const USAGE_META = {
  using:        { label: 'Active',     icon: 'check-circle-fill', bg: 'rgba(26,127,78,0.12)',  fg: brand.green,     br: 'rgba(26,127,78,0.3)' },
  no_account:   { label: 'No Usage',   icon: 'x-circle',          bg: 'rgba(192,57,43,0.10)',  fg: brand.red,       br: 'rgba(192,57,43,0.3)' },
};

function UsageBadge({ status }) {
  const m = USAGE_META[status];
  if (!m) return <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>;
  return (
    <span
      title={status === 'using'
        ? 'Spent on Claude this month'
        : 'No Claude spend this month'}
      style={{
        background: m.bg,
        color: m.fg,
        border: `1px solid ${m.br}`,
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <i className={`bi bi-${m.icon} me-1`} />{m.label}
    </span>
  );
}
UsageBadge.propTypes = { status: PropTypes.string };

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—';
const fmtDateTime = fmtDate;

export default function ClaudeActivations() {
  const { data: submissions = [], isLoading, refetch } = useListClaudeSubmissionsQuery();
  const { data: unmatchedData } = useListUnmatchedClaudeAccountsQuery();
  const [triggerStatus] = useUpdateClaudeSubmissionStatusMutation();
  const [triggerCertUrl] = useGetClaudeCertificateUrlMutation();

  const [filter, setFilter] = useState('all');
  const [usageFilter, setUsageFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [firmFilter, setFirmFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const firmOptions = useMemo(
    () => Array.from(new Set(submissions.map(s => s.firm).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [submissions],
  );

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return submissions.filter(s => {
      if (filter !== 'all' && s.status !== filter) return false;
      // Claude account status (live, current month). `null` means usage
      // reporting isn't configured — those rows are only excluded when a
      // specific account status is requested.
      if (usageFilter !== 'all' && s.claude_usage_status !== usageFilter) return false;
      if (!matchesTier(s.activity_30d, tierFilter)) return false;
      if (firmFilter !== 'all' && (s.firm || '') !== firmFilter) return false;
      if (q && !`${s.final_email || ''} ${s.username || ''} ${s.first_name || ''} ${s.last_name || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [submissions, filter, usageFilter, tierFilter, firmFilter, searchQuery]);

  const stats = useMemo(() => ({
    total:       submissions.length,
    notStarted:  submissions.filter(s => s.status === 'not_started').length,
    pending:     submissions.filter(s => s.status === 'pending').length,
    completed:   submissions.filter(s => s.status === 'completed').length,
  }), [submissions]);

  // Unique emails of the currently-filtered rows, for the "Copy emails" button.
  const filteredEmails = useMemo(
    () => Array.from(new Set(filtered.map(s => (s.final_email || s.username || '').trim().toLowerCase()).filter(Boolean))),
    [filtered],
  );

  const copyEmails = async () => {
    if (!filteredEmails.length) return;
    try {
      await navigator.clipboard.writeText(filteredEmails.join('; '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      window.alert('Could not copy to clipboard.');
    }
  };

  const handleStatusToggle = async (sub) => {
    const next = sub.status === 'completed' ? 'pending' : 'completed';
    setUpdatingId(sub.id);
    try {
      await triggerStatus({ id: sub.id, status: next }).unwrap();
      await refetch();
    } catch (_e) {
      // surface errors via the badge — leaves the row in its previous state
    }
    setUpdatingId(null);
  };

  const handleViewCertificate = async (sub) => {
    try {
      const res = await triggerCertUrl({ id: sub.id }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (_e) {
      // no-op; button will remain enabled for retry
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>

      {/* Header */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.5rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.65rem', flexShrink: 0 }}>
            <i className="bi bi-shield-check" style={{ color: brand.goldenrod, fontSize: '1.5rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Compliance
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: 0 }}>Claude Activations</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: '4px 0 0', fontSize: '0.85rem' }}>
              Review and approve Claude Enterprise account activation requests.
            </p>
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="row g-3 mb-3">
        {[
          { label: 'Total',         value: stats.total },
          { label: 'Not Started',   value: stats.notStarted },
          { label: 'Pending Review', value: stats.pending },
          { label: 'Completed',     value: stats.completed },
        ].map(s => (
          <div key={s.label} className="col-md-3">
            <div style={{ background: 'white', borderRadius: '12px', padding: '1rem 1.25rem', boxShadow: '0 2px 12px rgba(10,41,71,0.08)' }}>
              <div style={{ color: brand.charcoal, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>{s.label}</div>
              <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.2 }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Usage distribution — engagement tiers by 30-day activity */}
      {filtered.some(s => s.activity_30d != null) && (
        <div className="mb-3">
          <UsageTierChart
            counts={filtered.map(s => s.activity_30d || 0)}
            title="Usage Distribution"
            subtitle="Team members grouped into engagement tiers by activity (messages + Claude Code sessions) in the last 30 days."
          />
        </div>
      )}

      {/* Submissions table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <i className="bi bi-list-check" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Submissions</h5>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Showing {filtered.length === submissions.length ? submissions.length : `${filtered.length} of ${submissions.length}`}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search user"
              aria-label="Search user"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', width: '180px' }}
            />
            {firmOptions.length > 0 && (
              <select
                value={firmFilter}
                onChange={e => setFirmFilter(e.target.value)}
                aria-label="Filter by firm"
                title="Firm"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}
              >
                <option value="all" style={{ color: brand.charcoal }}>All firms</option>
                {firmOptions.map(f => <option key={f} value={f} style={{ color: brand.charcoal }}>{f}</option>)}
              </select>
            )}
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              aria-label="Filter by certification status"
              title="Certification status"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              <option value="all"         style={{ color: brand.charcoal }}>All statuses</option>
              <option value="not_started" style={{ color: brand.charcoal }}>Not Started</option>
              <option value="pending"     style={{ color: brand.charcoal }}>Pending</option>
              <option value="completed"   style={{ color: brand.charcoal }}>Completed</option>
            </select>
            <select
              value={usageFilter}
              onChange={e => setUsageFilter(e.target.value)}
              aria-label="Filter by Claude account status"
              title="Claude account status"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              <option value="all"          style={{ color: brand.charcoal }}>Any usage</option>
              <option value="using"        style={{ color: brand.charcoal }}>Active</option>
              <option value="no_account"   style={{ color: brand.charcoal }}>No Usage</option>
            </select>
            {submissions.some(s => s.activity_30d != null) && (
              <select
                value={tierFilter}
                onChange={e => setTierFilter(e.target.value)}
                aria-label="Filter by usage category"
                title="Usage category"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', cursor: 'pointer' }}
              >
                <option value="all" style={{ color: brand.charcoal }}>All categories</option>
                {USAGE_TIERS.map(t => <option key={t.key} value={t.key} style={{ color: brand.charcoal }}>{t.label}</option>)}
              </select>
            )}
            <button
              type="button"
              onClick={copyEmails}
              disabled={filteredEmails.length === 0}
              title="Copy the email addresses of the filtered people"
              style={{ background: copied ? brand.green : brand.goldenrod, color: copied ? 'white' : brand.deepBlue, border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, cursor: filteredEmails.length ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', opacity: filteredEmails.length ? 1 : 0.6 }}
            >
              <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
              {copied ? 'Copied!' : `Copy emails (${filteredEmails.length})`}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
            <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
            No submissions match the current filters.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
              <thead style={{ background: brand.softGray }}>
                <tr>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>User</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Steps</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>Submitted</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Cert</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Status</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>Claude Usage</th>
                  <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>Reviewed</th>
                  <th style={{ padding: '0.5rem 0.75rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(sub => {
                  const isCompleted  = sub.status === 'completed';
                  const isNotStarted = sub.status === 'not_started';
                  const isUpdating   = updatingId === sub.id;
                  const fullName = `${sub.first_name || ''} ${sub.last_name || ''}`.trim();
                  return (
                    <tr key={sub.id}>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: brand.deepBlue, whiteSpace: 'nowrap' }}>
                            {fullName || sub.final_email || sub.username || '—'}
                            <ClaudeCodeMark show={sub.uses_claude_code} />
                          </span>
                          <TierBadge count={sub.activity_30d} />
                        </div>
                        {fullName && (sub.final_email || sub.username) && (
                          <div style={{ color: brand.charcoal, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                            {sub.final_email || sub.username}
                          </div>
                        )}
                        {sub.firm && (
                          <div style={{ color: brand.tealBlue, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {sub.firm}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                        <span title="Policies confirmed" style={{ marginRight: 6 }}>
                          <i className={`bi ${sub.policies_confirmed ? 'bi-check-circle-fill' : 'bi-circle'}`} style={{ color: sub.policies_confirmed ? brand.green : brand.charcoal }} />
                        </span>
                        <span title="Training confirmed" style={{ marginRight: 6 }}>
                          <i className={`bi ${sub.training_confirmed ? 'bi-check-circle-fill' : 'bi-circle'}`} style={{ color: sub.training_confirmed ? brand.green : brand.charcoal }} />
                        </span>
                        <span title="Certificate uploaded">
                          <i className={`bi ${sub.certificate_s3_key ? 'bi-check-circle-fill' : 'bi-circle'}`} style={{ color: sub.certificate_s3_key ? brand.green : brand.charcoal }} />
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal, whiteSpace: 'nowrap' }}>{fmtDate(sub.submitted_at)}</td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        {sub.certificate_s3_key ? (
                          <button
                            onClick={() => handleViewCertificate(sub)}
                            title="View certificate"
                            style={{ background: 'transparent', border: `1px solid ${brand.tealBlue}`, color: brand.tealBlue, borderRadius: '6px', padding: '3px 8px', fontSize: '0.78rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <i className="bi bi-file-earmark-arrow-down" />
                          </button>
                        ) : (
                          <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        <StatusBadge status={sub.status} />
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        <UsageBadge status={sub.claude_usage_status} />
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle', color: brand.charcoal, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {sub.reviewed_at ? fmtDateTime(sub.reviewed_at) : '—'}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', verticalAlign: 'middle' }}>
                        {isNotStarted ? (
                          <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>—</span>
                        ) : (
                        <button
                          onClick={() => handleStatusToggle(sub)}
                          disabled={isUpdating}
                          title={isCompleted ? 'Reopen' : 'Complete'}
                          style={{
                            background: isCompleted ? 'transparent' : brand.green,
                            color: isCompleted ? brand.charcoal : 'white',
                            border: isCompleted ? `1px solid ${brand.charcoal}` : 'none',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: isUpdating ? 'wait' : 'pointer',
                            opacity: isUpdating ? 0.6 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                            minWidth: '105px',
                          }}
                        >
                          {isUpdating
                            ? <><span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} /> Saving</>
                            : isCompleted
                              ? <><i className="bi bi-arrow-counterclockwise" /> Reopen</>
                              : <><i className="bi bi-check2-circle" /> Complete</>
                          }
                        </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.some(s => s.uses_claude_code) && (
        <div style={{ marginTop: '0.6rem', color: brand.charcoal, fontSize: '0.75rem' }}>
          <span style={{ color: brand.goldenrod, fontWeight: 800 }}>*</span> also uses Claude Code
        </div>
      )}

      {/* ── Reverse check: Claude accounts not tied to an active employee ──────── */}
      {unmatchedData?.configured && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginTop: '1.5rem' }}>
          <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.red}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="bi bi-exclamation-triangle" style={{ color: brand.red }} />
            <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Unmatched Claude Accounts</h5>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600 }}>{(unmatchedData.unmatched || []).length}</span>
          </div>

          <div style={{ padding: '0.75rem 1.25rem 0', color: brand.charcoal, fontSize: '0.8rem' }}>
            Claude accounts whose email doesn&apos;t match any <strong>active</strong> employee — review for orphaned (former-employee) or external access.
            {unmatchedData.members_available === false && (
              <span style={{ color: brand.charcoal, opacity: 0.85 }}> Showing accounts with spend this month; a full-roster check (including idle $0 accounts) needs Admin&nbsp;API access, which isn&apos;t enabled for this organization.</span>
            )}
          </div>

          {(unmatchedData.unmatched || []).length === 0 ? (
            <div style={{ padding: '1.5rem 1.25rem', textAlign: 'center', color: brand.green, fontSize: '0.85rem' }}>
              <i className="bi bi-check-circle me-1" /> Every known Claude account maps to an active employee.
            </div>
          ) : (
            <div className="table-responsive" style={{ marginTop: '0.5rem' }}>
              <table className="table table-hover mb-0" style={{ fontSize: '0.825rem' }}>
                <thead style={{ background: brand.softGray }}>
                  <tr>
                    <th style={{ padding: '0.5rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Account email</th>
                    <th style={{ padding: '0.5rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Source</th>
                    <th style={{ padding: '0.5rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'right' }}>Spend (MTD)</th>
                  </tr>
                </thead>
                <tbody>
                  {(unmatchedData.unmatched || []).map(a => (
                    <tr key={a.email}>
                      <td style={{ padding: '0.5rem 1.25rem', verticalAlign: 'middle', fontWeight: 600, color: brand.deepBlue, whiteSpace: 'nowrap' }}>{a.email}</td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: a.is_member ? brand.charcoal : brand.goldenrod }}>
                          {a.is_member ? 'Org member' : 'Spend only'}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 1.25rem', verticalAlign: 'middle', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: a.cost_usd > 0 ? brand.deepBlue : brand.charcoal }}>
                        ${Number(a.cost_usd || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
